<template>
    <CCard class="mb-0 h-100" v-show="!storePageLoading">
        <CCardHeader class="object-header">
            <CRow class="align-items-center">
                <CCol md="4">
                    <!-- Title page  -->
                    <h1 class="page__title justify-content">
                        {{ $t(titlePage) }}
                    </h1>
                </CCol>
                <CCol md="8">
                    <!--
                        El rango de fechas y las acciones viven en la cabecera:
                        todo lo que hay debajo esta acotado por ese periodo.
                    -->
                    <div
                        class="details-header__actions d-flex flex-wrap justify-content-end align-items-center"
                    >
                        <div class="details-header__filter">
                            <FilterDate @updateData="updateData" />
                        </div>
                        <CButton
                            color="primary"
                            class="ml-2 button shadow"
                            @click="
                                $router.push({
                                    name: 'delivery_campaign_group_logs',
                                    params: { uuid },
                                })
                            "
                        >
                            {{ $t("Logs") }}
                        </CButton>
                        <ButtonBack />
                    </div>
                </CCol>
            </CRow>
        </CCardHeader>
        <CCardBody class="main-card-body">
            <CContainer fluid class="p-0">
                <!-- 1. Delivery summary -->
                <CRow class="p-2 m-0">
                    <CCol md="12">
                        <CCard class="position-relative">
                            <CCardHeader>
                                <strong>{{ $t("delivery_summary") }}</strong>
                            </CCardHeader>
                            <CCardBody>
                                <CRow>
                                    <CCol
                                        v-for="kpi in globalKpis"
                                        :key="kpi.key"
                                        :md="kpiColMd"
                                        sm="6"
                                        class="mb-3"
                                    >
                                        <!--
                                            Los contadores que el endpoint de
                                            drill-down acepta como `outcome`
                                            abren la lista de leads; el resto
                                            (attempts) no es un outcome.
                                        -->
                                        <RouterLink
                                            v-if="kpi.to"
                                            :to="kpi.to"
                                            class="balancer-kpi__link"
                                        >
                                            <BalancerKpi :kpi="kpi" />
                                        </RouterLink>
                                        <BalancerKpi v-else :kpi="kpi" />
                                    </CCol>
                                </CRow>
                                <p
                                    v-if="showKpiNote"
                                    class="balancer-kpi__note mb-0"
                                >
                                    {{
                                        $t(
                                            "Attempts also include secondary outcomes (capped, stopped, already_sent, async_queued), so it is not the sum of the counters above"
                                        )
                                    }}
                                </p>
                            </CCardBody>
                            <CElementCover
                                :opacity="0.8"
                                class="bg-trasparent"
                                v-if="stats.loading"
                            />
                        </CCard>
                    </CCol>
                </CRow>
                <!-- 2. Performance by output -->
                <CRow class="p-2 m-0">
                    <CCol md="12">
                        <BalancerOutputsPerformance
                            :outputs="outputsPerformance.items"
                            :loading="outputsPerformance.loading"
                            :error="outputsPerformance.error"
                        />
                    </CCol>
                </CRow>
            </CContainer>
        </CCardBody>
    </CCard>
</template>

<script>
/**
 * Components
 */
import ButtonBack from "£lement/ButtonBack";
import FilterDate from "~/components/FilterDate";
import BalancerOutputsPerformance from "./components/BalancerOutputsPerformance.vue";
import BalancerKpi from "./components/BalancerKpi.vue";
import { isBalancerOutcome } from "~/utils/balancerOutcomes";

/**
 * Mixins
 */
import pageMixin from "~/mixins/page.js";
import tableMixin from "~/mixins/table.js";

export default {
    name: "DeliveryCampaignGroupDetailPage",
    middleware: ["auth", "page"],
    mixins: [pageMixin, tableMixin],
    components: {
        FilterDate,
        ButtonBack,
        BalancerOutputsPerformance,
        BalancerKpi,
    },
    data() {
        return {
            stats: {
                loading: false,
                global: {},
            },
            outputsPerformance: {
                loading: false,
                items: [],
                error: null,
            },
        };
    },
    computed: {
        titlePage: function () {
            return this.$route.meta.title
                ? this.$route.meta.title
                : this.$route.name;
        },
        uuid: function () {
            return this.$route.params.uuid;
        },
        /**
         * Contadores globales del grupo.
         *
         * Solo llevan porcentaje y barra los que componen `attempts`
         * (`ok`, `rejected`, `duplicate`, `error`, `capped`). `attempts` es el
         * denominador y `not_delivered_any` se cuenta por lead sobre otro
         * evento, asi que un % sobre intentos seria enganoso en ambos.
         *
         * Los contadores nuevos se pintan solo si el endpoint los devuelve, de
         * modo que la vista funciona antes y despues de que el backend los
         * anada.
         */
        globalKpis: function () {
            const global = this.stats.global || {};
            const base = this.toCount(global.attempts);
            const has = (key) =>
                global[key] !== undefined && global[key] !== null;

            const kpi = (
                key,
                tone,
                { muted = false, rated = false, hint = null, label = null } = {}
            ) => {
                const value = this.toCount(global[key]);
                const drillable = isBalancerOutcome(key);
                // Sin intentos no hay proporcion: se pinta un guion, no 0 %.
                const percent =
                    rated && base > 0
                        ? this.calculatePercentage(value, base)
                        : null;
                return {
                    key,
                    // La etiqueta puede no coincidir con el campo: `attempts`
                    // se lee "Total".
                    label: label || key,
                    to: drillable
                        ? {
                              name: "delivery_campaign_group_leads_by_outcome",
                              params: { uuid: this.uuid, outcome: key },
                          }
                        : null,
                    value,
                    tone,
                    muted,
                    hint,
                    percent,
                    percentLabel:
                        percent === null ? "—" : this.formatPercent(percent),
                    percentRounded: percent === null ? 0 : Math.round(percent),
                    // Redondeado: el style inline no necesita 15 decimales.
                    barWidth: `${
                        percent === null ? 0 : Math.round(percent * 100) / 100
                    }%`,
                };
            };

            // El orden cuenta el flujo de entrega: cuantos se intentaron, como
            // acabaron, y que queda en el aire.
            const kpis = [
                kpi("attempts", "neutral", {
                    label: "total_attempts",
                    hint: "attempts_hint",
                }),
                kpi("ok", "success", { rated: true }),
                kpi("rejected", "warning", { rated: true }),
            ];

            // Mismo orden que el desglose del contrato:
            // ok / rejected / duplicate / error / capped. `duplicate` es
            // contador propio desde que `rejected` quedo como KO real.
            if (has("duplicate")) {
                kpis.push(
                    kpi("duplicate", "warning", {
                        rated: true,
                        hint: "duplicate_hint",
                    })
                );
            }
            kpis.push(kpi("error", "danger", { rated: true }));

            if (has("capped")) {
                kpis.push(
                    kpi("capped", "warning", {
                        rated: true,
                        hint: "capped_hint",
                    })
                );
            }
            // Aqui `pending` es `capped` + OTP pendiente, del log del
            // balanceador, y es subconjunto de `attempts`. El tooltip lo
            // aclara, porque el `pending` de /stats/overview y de
            // /outputs/performance son los parados en `delivery_pending`.
            //
            // Va SIN porcentaje a proposito: solapa con `capped`, asi que un %
            // sobre `attempts` contaria dos veces los mismos envios y la fila
            // dejaria de sumar 100 %.
            if (has("pending")) {
                // La linea de debajo lleva los leads distintos en vez de un %:
                // `pending` cuenta envios, asi que un lead parado en N salidas
                // suma N. Y un % aqui contaria dos veces los `capped`.
                kpis.push({
                    ...kpi("pending", "info", {
                        muted: true,
                        hint: "pending_stats_hint",
                    }),
                    percentLabel: has("unique_pending")
                        ? `${this.toCount(global.unique_pending)} ${this.$t(
                              "unique_leads"
                          )}`
                        : "—",
                });
            }
            kpis.push(
                kpi("not_delivered_any", "neutral", {
                    muted: true,
                    hint: "not_delivered_any_hint",
                })
            );

            return kpis;
        },
        /**
         * Sin `capped` el desglose no puede cerrar contra `attempts`, y el
         * hueco queda sin explicar. Con el, la nota sobra.
         */
        showKpiNote: function () {
            const global = this.stats.global || {};
            return global.capped === undefined || global.capped === null;
        },
        /**
         * Seis tarjetas caben a `md="2"`; por encima se pasa a cuatro por fila
         * para que la segunda fila no quede coja.
         */
        kpiColMd: function () {
            return this.globalKpis.length > 6 ? "3" : "2";
        },
        /**
         * El endpoint devuelve todos los contadores como string: se castean
         * antes de pintarlos y de calcular los porcentajes derivados.
         */
    },
    watch: {},
    methods: {
        toCount(value) {
            const count = parseInt(value, 10);
            return isNaN(count) ? 0 : count;
        },
        formatPercent(value) {
            return `${value.toFixed(1)}%`;
        },
        async loadStats() {
            this.stats.loading = true;
            try {
                const response = await this.fetcher({
                    url: `/shipment.balancing.api/client-groups/${this.uuid}/stats`,
                    query: {
                        from: this.storeFrom,
                        to: this.storeTo,
                    },
                });
                this.stats.global = response.global || {};
            } catch (error) {
                this.stats.global = {};
            }
            this.stats.loading = false;
        },
        /**
         * Rendimiento por salida. Endpoint aparte de `/stats`: trae las mismas
         * cifras por salida mas `error_rate` y los motivos de rechazo, y los
         * contadores ya casteados a int.
         */
        async loadOutputsPerformance() {
            this.outputsPerformance.loading = true;
            this.outputsPerformance.error = null;
            try {
                const response = await this.fetcher({
                    url: `/shipment.balancing.api/client-groups/${this.uuid}/outputs/performance`,
                    query: {
                        from: this.storeFrom,
                        to: this.storeTo,
                        top_reasons: 5,
                    },
                });
                this.outputsPerformance.items = response.outputs || [];
                this.outputsPerformance.error = null;
            } catch (error) {
                // No se silencia: si el endpoint no responde el panel lo dice,
                // en vez de quedarse vacio y parecer que no hay datos.
                this.outputsPerformance.items = [];
                this.outputsPerformance.error =
                    (error && error.response && error.response.status) ||
                    (error && error.message) ||
                    true;
            }
            this.outputsPerformance.loading = false;
        },
        async updateData() {
            await Promise.all([
                this.loadStats(),
                this.loadOutputsPerformance(),
            ]);

            this.$store.dispatch("page/updateLoading", {
                loading: false,
            });
        },
    },
    mounted() {
        this.updateData();
    },
};
</script>

<style scoped lang="scss">
// Paleta semantica del tema de CoreUI 3, explicita para no depender de
// variables CSS que el tema puede no exponer.
// La tarjeta de KPI vive en `components/BalancerKpi.vue`. Aqui solo queda la
// nota que acompana a la fila, que no es parte de la tarjeta.
// El enlace envuelve la tarjeta: no debe tener aspecto de enlace, la pista es
// el cursor y el realce al pasar por encima.
.balancer-kpi__link {
    display: block;
    height: 100%;
    color: inherit;
    text-decoration: none;

    &:hover {
        text-decoration: none;
    }
}

.balancer-kpi__note {
    font-size: 0.75rem;
    color: #768192;
}
</style>
